import Decimal from 'app/src/data/base/Decimal';
import { toBoolean, toFloat } from 'helpers/primitiveParsing';

export const CASH_ROUNDING_OPTIONS = {
  CASH_ROUNDING_ENABLED: 'cash_rounding_enabled',
  CASH_ROUNDING_DENOMINATION: 'cash_rounding_denomination',
  CASH_ROUNDING_RULE: 'cash_rounding_rule',
};

export const CASH_ROUNDING_RULES = {
  NEAREST_INCREMENT_MIDPOINT_UP: 'nearest_increment_midpoint_up',
  NEAREST_INCREMENT_MIDPOINT_DOWN: 'nearest_increment_midpoint_down',
  ALWAYS_ROUND_UP: 'always_round_up',
  ALWAYS_ROUND_DOWN: 'always_round_down',
};

const CashRoundingRules = [
  CASH_ROUNDING_RULES.NEAREST_INCREMENT_MIDPOINT_UP,
  CASH_ROUNDING_RULES.NEAREST_INCREMENT_MIDPOINT_DOWN,
  CASH_ROUNDING_RULES.ALWAYS_ROUND_UP,
  CASH_ROUNDING_RULES.ALWAYS_ROUND_DOWN,
] as const;

export type CashRoundingRule = (typeof CashRoundingRules)[number];

export interface CashRoundingOptions {
  cashRoundingEnabled: boolean;
  denomination: number;
  rule: CashRoundingRule;
}

/**
 * Calculates the rounded amount based on the cash rounding options
 * @param {Decimal | number | string} amount The original amount
 * @param {CashRoundingOptions} options The cash rounding options
 * @returns {Decimal} The rounded amount
 */
export function calculateCashRoundingAmount(
  amount: Decimal | number | string,
  options: CashRoundingOptions
): Decimal {
  if (!options) {
    throw new Error('Cash rounding options are required');
  }

  const denomination = new Decimal(options.denomination);
  const amountBN = new Decimal(amount);
  const isNegative = amountBN.isNegative();
  const absoluteAmount = amountBN.abs();
  const units = absoluteAmount.dividedBy(denomination);
  let roundedUnits: Decimal;

  switch (options.rule) {
    case CASH_ROUNDING_RULES.ALWAYS_ROUND_DOWN:
      roundedUnits = units.integerValue(Decimal.ROUND_FLOOR);
      break;
    case CASH_ROUNDING_RULES.ALWAYS_ROUND_UP:
      roundedUnits = units.integerValue(Decimal.ROUND_CEIL);
      break;
    case CASH_ROUNDING_RULES.NEAREST_INCREMENT_MIDPOINT_UP:
      roundedUnits = units.integerValue(Decimal.ROUND_HALF_UP);
      break;
    case CASH_ROUNDING_RULES.NEAREST_INCREMENT_MIDPOINT_DOWN:
      roundedUnits = units.integerValue(Decimal.ROUND_HALF_DOWN);
      break;
    default:
      roundedUnits = units.integerValue(Decimal.ROUND_HALF_UP);
  }

  const roundedAmount = roundedUnits.multipliedBy(denomination);
  return isNegative ? roundedAmount.negated() : roundedAmount;
}

/**
 * Calculates the delta between the original amount and the rounded amount.
 * Delta = original - rounded
 * Positive = customer pays less (rounded down), Negative = customer pays more (rounded up)
 * @param {Decimal | number | string} originalAmount The original amount
 * @param {CashRoundingOptions} options The cash rounding options
 * @returns {Decimal} The delta between the original amount and the rounded amount
 */
export function calculateCashRoundingDelta(
  originalAmount: Decimal | number | string,
  options: CashRoundingOptions
): Decimal {
  const rounded = calculateCashRoundingAmount(originalAmount, options);
  const original = new Decimal(originalAmount);
  return original.minus(rounded);
}

export const fetchCashRoundingOptions = async (): Promise<CashRoundingOptions | null> => {
  const response = await window.lsCloud.Options.get([
    CASH_ROUNDING_OPTIONS.CASH_ROUNDING_ENABLED,
    CASH_ROUNDING_OPTIONS.CASH_ROUNDING_DENOMINATION,
    CASH_ROUNDING_OPTIONS.CASH_ROUNDING_RULE,
  ]);

  if (!response) return null;

  let [cashRoundingEnabled, denomination, rule] = response;

  cashRoundingEnabled = toBoolean(cashRoundingEnabled);
  denomination = toFloat(denomination);
  rule = CashRoundingRules.includes(rule) ? (rule as CashRoundingRule) : null;

  if (!cashRoundingEnabled) return null;
  if (denomination <= 0) return null;
  if (!rule) return null;

  return {
    cashRoundingEnabled,
    denomination,
    rule,
  };
};
